class ProductDetailsAccordion extends HTMLElement {
  constructor() {
    super();
    this.trigger = this.querySelector('.product-details-accordion__trigger');
    this.panel = this.querySelector('.product-details-accordion__panel');
    this.handleClick = this.handleClick.bind(this);
  }

  connectedCallback() {
    if (!this.trigger || !this.panel) return;

    this.panel.hidden = this.trigger.getAttribute('aria-expanded') !== 'true';
    this.panel.style.height = this.panel.hidden ? '0px' : 'auto';
    this.trigger.addEventListener('click', this.handleClick);
  }

  disconnectedCallback() {
    this.trigger?.removeEventListener('click', this.handleClick);
  }

  handleClick() {
    const isOpen = this.trigger.getAttribute('aria-expanded') === 'true';
    if (isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    this.trigger.setAttribute('aria-expanded', 'true');
    this.panel.hidden = false;
    this.panel.style.height = '0px';

    requestAnimationFrame(() => {
      this.panel.style.height = `${this.panel.scrollHeight}px`;
    });

    this.panel.addEventListener(
      'transitionend',
      (event) => {
        if (event.propertyName === 'height') this.panel.style.height = 'auto';
      },
      { once: true }
    );
  }

  close() {
    this.trigger.setAttribute('aria-expanded', 'false');
    this.panel.style.height = `${this.panel.scrollHeight}px`;

    requestAnimationFrame(() => {
      this.panel.style.height = '0px';
    });

    this.panel.addEventListener(
      'transitionend',
      (event) => {
        if (event.propertyName === 'height') this.panel.hidden = true;
      },
      { once: true }
    );
  }
}

if (!customElements.get('product-details-accordion')) {
  customElements.define('product-details-accordion', ProductDetailsAccordion);
}

const productGalleryDesktopQuery = window.matchMedia('(min-width: 750px)');

function enhanceProductGallery(gallery) {
  if (gallery.dataset.productGalleryEnhanced === 'true') return;

  const viewer = gallery.querySelector('[id^="GalleryViewer"]');
  const mediaItems = Array.from(
    gallery.querySelectorAll('[id^="Slider-Gallery"] > .product__media-item[data-media-id]')
  );
  const thumbnailSlider = gallery.querySelector('[id^="GalleryThumbnails"]');
  const thumbnailList = thumbnailSlider?.querySelector('[id^="Slider-Thumbnails"]');
  const thumbnails = Array.from(thumbnailSlider?.querySelectorAll('[data-target]') || []);

  if (!viewer || mediaItems.length === 0 || !thumbnailSlider || !thumbnailList) return;

  const initialPrimaryId = mediaItems[0].dataset.mediaId;
  const initialSecondaryId = mediaItems[1]?.dataset.mediaId || initialPrimaryId;

  const setVisibleMedia = (activeId = initialPrimaryId) => {
    let secondaryId = initialSecondaryId;

    if (activeId === initialSecondaryId && initialPrimaryId !== initialSecondaryId) {
      secondaryId = initialPrimaryId;
    }

    mediaItems.forEach((mediaItem) => {
      const mediaId = mediaItem.dataset.mediaId;
      mediaItem.classList.toggle('product-media--primary', mediaId === activeId);
      mediaItem.classList.toggle('product-media--secondary', mediaId === secondaryId && mediaId !== activeId);
    });
  };

  thumbnails.forEach((thumbnail) => {
    thumbnail.querySelector('button')?.addEventListener('click', () => {
      if (!productGalleryDesktopQuery.matches) return;
      window.requestAnimationFrame(() => setVisibleMedia(thumbnail.dataset.target));
    });
  });

  const scrollThumbnails = (direction) => {
    const item = thumbnailList.querySelector('.thumbnail-list__item');
    const itemHeight = item?.getBoundingClientRect().height || 72;
    thumbnailList.scrollBy({ top: direction * (itemHeight + 10) * 3, behavior: 'smooth' });
  };

  thumbnailSlider.querySelector('.slider-button--prev')?.addEventListener(
    'click',
    (event) => {
      if (!productGalleryDesktopQuery.matches) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      scrollThumbnails(-1);
    },
    true
  );

  thumbnailSlider.querySelector('.slider-button--next')?.addEventListener(
    'click',
    (event) => {
      if (!productGalleryDesktopQuery.matches) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      scrollThumbnails(1);
    },
    true
  );

  setVisibleMedia();
  gallery.classList.add('product-gallery--enhanced');
  gallery.dataset.productGalleryEnhanced = 'true';
  window.requestAnimationFrame(() => setVisibleMedia());
}

function initializeProductGalleries(root = document) {
  root.querySelectorAll('media-gallery[data-desktop-layout="thumbnail_slider"]').forEach(enhanceProductGallery);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initializeProductGalleries());
} else {
  initializeProductGalleries();
}

document.addEventListener('shopify:section:load', (event) => initializeProductGalleries(event.target));
